import { NodeOperation } from "@/shared/types/projectData";
import {
  WebSocketState,
  QueuedOperation,
  SyncState,
  WebSocketManagerConfig,
} from "@/shared/apiTypes";
import { getNodeId } from "@/features/projects/MultiProjectContext";
import { getAuthRuntimeAccessToken, getAuthRuntimeSessionIdentity } from "@/features/auth/services/authRuntime";
import { ensureAuthRuntimeTokenStatus } from "@/features/auth/services/authRuntime";
import { log } from "@/shared/utils/logger";
import { getAuthToken } from "@/features/auth/services/authService";
import {embedMessageFields,embedRequested,getEmbedLease} from '@/features/saiEmbed/embedAccess';
import { getAdminViewAsUserId } from "@/features/auth/services/adminViewAs";
import { transformNodeToBackend } from "@/shared/utils/fieldNameTransform";

// Import extracted modules
import {
  dispatchMessage,
  MessageHandlerContext,
  isWebSocketMsg,
  type WebSocketMsg,
} from "./handlers/messageHandlers";
import { QueueManager, isNodeOperation } from "./utils/queueManager";
import { MessageDeduplicator } from "./utils/messageDeduplication";
import { SyncRecoveryManager } from "./utils/syncRecovery";
import { ReconnectionManager } from "./utils/reconnectionManager";
import { HeartbeatManager } from "./utils/heartbeatManager";
import { SubscriptionManager } from "./utils/subscriptionManager";
import { resolveQueuedOperationIdentity } from "./utils/queuedOperationIdentity";

import { API_BASE_URL } from '@/shared/apiClient';
import { OperationJournal, OperationJournalError, journalKey, validateReceiptQuery, type JournalRecord } from './utils/operationJournal';

export interface OperationReviewItem {
  owner: PrincipalContext;
  operationId: string;
  projectId: string;
  operation: Record<string, unknown>;
  timestamp: number;
  outcome: 'rejected' | 'partial' | 'conflict' | 'unconfirmed' | 'committed';
}

export interface OperationSendOptions {
  queueOnly?: boolean;
  owner?: PrincipalContext;
  /** Runs only after matching committed receipt validation (or explicit server readback review). */
  onCommitted?: () => void;
}

export interface DurableOperationOptions {
  journal?: OperationJournal;
  /** Root may enable only after transactional server and browser qualification. */
  transactionalReceiptsQualified?: boolean;
}

// Re-export types for backward compatibility
export type { WebSocketState, QueuedOperation, SyncState };

export interface PrincipalContext { userId: string | null; generation: number; }

let nextOwnerGeneration = 0;

export class WebSocketManager {
  private readonly journal: OperationJournal;
  private commitListeners = new Map<string, { record: JournalRecord; current: () => boolean; notify: () => void }>();
  private readonly transactionalReceiptsQualified: boolean;
  private reconciliationRequested = new Set<WebSocket>();
  private durableOperations = new Map<string, JournalRecord>();
  private admittingOperations = new Map<string, { owner: PrincipalContext; operation: QueuedOperation }>();
  private journalErrors = new Map<string, Error>();
  private reconciliationJobs = new Map<WebSocket, Promise<void>>();
  private ownerGeneration = 0;
  private uncertainOperations = new Map<string, QueuedOperation[]>();
  private awaitingAcknowledgements = new Map<string, {
    operation: QueuedOperation; ownerId: string; socket: WebSocket; timer: ReturnType<typeof setTimeout>;
  }>();
  private readonly ACKNOWLEDGEMENT_TIMEOUT_MS = 15_000;
  private switchingUser = false;
  private acknowledgementSessions = new Map<string, string>();
  private outboundSyncStatus: SyncState["syncStatus"] = "synced";
  private desiredProjects: Set<string> | null = null;
  private ws: WebSocket | null = null;
  private config: Required<WebSocketManagerConfig>;
  private state: WebSocketState = "disconnected";
  private manualDisconnect = false;
  private socketAuthReady = false;
  private socketIdentity: { socket: WebSocket; token: string; authSession: string | null; viewAs: string | null } | null = null;
  private embedRefreshCleanup: (()=>void)|null=null;
  private syncState: SyncState = {
    localVersion: 0,
    serverVersion: 0,
    lastSyncTime: 0,
    pendingOperations: [],
    syncStatus: "synced",
  };

  // Multi-project support
  private currentUserId: string | null = null;
  private currentSessionId: string | null = null;

  // Constants
  private readonly MAX_QUEUE_SIZE = 1000;
  private readonly MAX_UNPARSEABLE_MESSAGES = 10;
  private readonly MAX_MESSAGE_SIZE = 500;

  // Extracted managers
  private queueManager: QueueManager;
  private queueOwnerId: string | null = null;
  private userQueues = new Map<string, { queue: QueueManager; syncState: SyncState; outboundSyncStatus: SyncState["syncStatus"] }>();
  private messageDeduplicator: MessageDeduplicator;
  private syncRecoveryManager: SyncRecoveryManager;
  private reconnectionManager: ReconnectionManager;
  private heartbeatManager: HeartbeatManager;
  private subscriptionManager: SubscriptionManager;

  constructor(config: WebSocketManagerConfig, durable: DurableOperationOptions = {}) {
    this.journal = durable.journal ?? new OperationJournal();
    this.transactionalReceiptsQualified = durable.transactionalReceiptsQualified === true;
    this.config = {
      reconnectInterval: 1000,
      maxReconnectDelay: 30000,
      maxReconnectAttempts: 0,
      maxRetries: 3,
      onStateChange: () => {},
      onSyncStateChange: () => {},
      onMessage: () => {},
      onError: () => {},
      ...config,
    };

    // Initialize managers
    this.queueManager = new QueueManager({
      maxQueueSize: this.MAX_QUEUE_SIZE,
      maxRetries: this.config.maxRetries,
      onSyncStateChange: (updates) => this.updateSyncState(updates),
    });

    this.messageDeduplicator = new MessageDeduplicator();

    this.syncRecoveryManager = new SyncRecoveryManager({
      maxUnparseableMessages: this.MAX_UNPARSEABLE_MESSAGES,
      maxMessageSize: this.MAX_MESSAGE_SIZE,
      getCurrentProjectIds: () => this.getSubscribedProjects(),
      getOwnerGeneration: () => this.ownerGeneration,
      onSyncStateChange: () => this.publishSyncState(),
    });

    this.reconnectionManager = new ReconnectionManager({
      reconnectInterval: this.config.reconnectInterval,
      maxReconnectDelay: this.config.maxReconnectDelay,
      maxRetries: this.config.maxReconnectAttempts,
    });

    this.heartbeatManager = new HeartbeatManager(30000);
    this.subscriptionManager = new SubscriptionManager();
  }

  updateCallbacks(config: Pick<WebSocketManagerConfig, "onStateChange" | "onSyncStateChange" | "onMessage" | "onError">): void {
    this.config = {
      ...this.config,
      ...config,
    };
  }

  private hasAuthToken(): boolean {
    return Boolean(getAuthRuntimeAccessToken() || getAuthToken());
  }

  getPrincipalContext(): PrincipalContext {
    return { userId: this.currentUserId, generation: this.ownerGeneration };
  }

  isCurrentPrincipal(owner: PrincipalContext): boolean {
    return Boolean(owner.userId) && owner.userId === this.currentUserId && owner.generation === this.ownerGeneration;
  }

  reconcileSubscriptions(projectIds: string[], owner: PrincipalContext): void {
    if (!this.isCurrentPrincipal(owner)) return;
    this.desiredProjects = new Set(projectIds.filter(id => id && id !== 'default'));
    for (const id of this.getSubscribedProjects()) {
      if (!this.desiredProjects.has(id)) this.unsubscribeFromProject(id);
    }
    for (const id of this.desiredProjects) this.subscribeToProject(id);
  }

  setUserContext(userId: string): void {
    if (this.switchingUser) return;
    // Repeated principal activation is a no-op. Publishing here feeds provider
    // state back into its connection effect and can prevent the first socket.
    if (this.currentUserId === userId && this.queueOwnerId === userId) return;
    if (this.currentUserId !== userId) this.ownerGeneration = ++nextOwnerGeneration;
    if (this.queueOwnerId && this.queueOwnerId !== userId) {
      this.switchingUser = true;
      this.disconnect();
      // Empty accounts need no retained queue or sync-state snapshot.
      if (this.queueManager.getOperationQueueLength() || this.queueManager.getMessageQueueLength()) {
        this.userQueues.set(this.queueOwnerId, { queue: this.queueManager, syncState: this.syncState, outboundSyncStatus: this.outboundSyncStatus });
      }
      const saved = this.userQueues.get(userId);
      this.userQueues.delete(userId);
      this.queueManager = saved?.queue ?? new QueueManager({
        maxQueueSize: this.MAX_QUEUE_SIZE,
        maxRetries: this.config.maxRetries,
        onSyncStateChange: (updates) => this.updateSyncState(updates),
      });
      this.outboundSyncStatus = saved?.outboundSyncStatus ?? "synced";
      this.syncState = saved?.syncState ?? {
        localVersion: 0,
        serverVersion: 0,
        lastSyncTime: 0,
        pendingOperations: [],
        syncStatus: "synced",
      };
      this.subscriptionManager.clear();
      this.desiredProjects = null;
      this.messageDeduplicator.clear();
      this.syncRecoveryManager.clearUnparseableMessages();
      this.reconnectionManager.resetAttempts();
      this.currentSessionId = null;
      this.switchingUser = false;
    }
    this.queueOwnerId = userId;
    this.currentUserId = userId;
    this.publishSyncState();
  }

  connect(sessionId?: string, userId?: string, projectId?: string): void {
    // Check if we have valid credentials before attempting connection
    if (!sessionId && !this.currentSessionId) {
      log.warn("WebSocket", "No session ID available, skipping connection");
      this.setState("disconnected");
      return;
    }

    if (!userId && !this.currentUserId) {
      log.warn("WebSocket", "No user ID available, skipping connection");
      this.setState("disconnected");
      return;
    }

    // Store credentials before the token guard so a later valid-token connect
    // can reuse the same session/user context without opening a tokenless socket.
    if (this.switchingUser) return;
    if (userId) this.setUserContext(userId);
    if (userId && this.currentUserId !== userId) return;
    if (sessionId && sessionId !== this.currentSessionId && this.ws) this.disconnect();
    if (sessionId) this.currentSessionId = sessionId;
    if (projectId) this.subscriptionManager.subscribe(projectId);

    if (this.state === "connecting" || this.state === "connected") {
      return;
    }

    if (!this.hasAuthToken()) {
      log.debug("WebSocket", "No auth token available yet, deferring websocket connection");
      this.setState("disconnected");
      return;
    }

    this.manualDisconnect = false;
    const connectingOwner = this.getPrincipalContext();
    this.setState("connecting");
    if (!this.isCurrentPrincipal(connectingOwner) || this.manualDisconnect || this.ws) return;

    try {
      const urlObj = new URL(this.config.url, window.location.href);
      const owner = this.getPrincipalContext();
      const socket = new WebSocket(urlObj.toString());
      this.ws = socket;
      this.socketAuthReady = false;

      socket.onopen = () => {
        void (async () => {
        if (!this.isCurrentPrincipal(owner) || this.ws !== socket) {
          socket.close();
          return;
        }

        // Only log reconnection success if we had attempts
        if (this.reconnectionManager.getAttemptCount() > 0) {
          log.info(
            "WebSocket",
            "🔄 RECONNECTED successfully after disconnect"
          );
          log.info(
            "WebSocket",
            "Projects to re-subscribe:",
            this.subscriptionManager.getSubscribedProjects()
          );
          log.info("WebSocket", "Queued messages:", this.queueManager.getMessageQueueLength());
        }
        // Send authentication message with JWT token
        if (this.currentSessionId && this.currentUserId) {
          const selectedProjectId =
            (projectId && (!this.desiredProjects || this.desiredProjects.has(projectId)) ? projectId : null) || this.subscriptionManager.getFirstProject() || "default";

          // CRITICAL: Add the projectId to subscriptions if not already there
          if (selectedProjectId && selectedProjectId !== "default") {
            this.subscriptionManager.subscribe(selectedProjectId);
            log.info(
              "WebSocket",
              `📌 Added project ${selectedProjectId} to subscriptions during auth`
            );
          }

          // Get JWT token from authService
          const tokenStatus = await ensureAuthRuntimeTokenStatus().catch(() => "transient");
          if (!this.isCurrentPrincipal(owner) || this.ws !== socket || socket.readyState !== WebSocket.OPEN || this.manualDisconnect) {
            return;
          }
          const token = embedRequested()?getEmbedLease()?.token:(getAuthRuntimeAccessToken() || getAuthToken());
          if (tokenStatus === "transient" && token) {
            log.warn(
              "WebSocket",
              "Auth runtime status is transient but token exists; continuing websocket authentication to avoid reconnect churn"
            );
          }
          if (tokenStatus === "transient" && !token) {
            log.debug(
              "WebSocket",
              "Auth refresh is temporarily unavailable during websocket authentication and no token is available; deferring authenticate payload until the next reconnect cycle"
            );
            if (this.ws === socket) {
              socket.close(1000, "Auth refresh unavailable");
            }
            return;
          }
          if ((tokenStatus === "unauthorized" || tokenStatus === "unavailable") && !token) {
            log.debug(
              "WebSocket",
              "Missing valid auth token during websocket authentication; closing socket without authenticate payload"
            );
            if (this.ws === socket) {
              socket.close(1000, "Missing auth token");
            }
            return;
          }
          if (!token) {
            log.debug(
              "WebSocket",
              "No auth token available during websocket authentication; closing socket without authenticate payload"
            );
            if (this.ws === socket) {
              socket.close(1000, "Missing auth token");
            }
            return;
          }

          const adminActAsUserId = getAdminViewAsUserId();
          const authMessage = {
            type: "authenticate",
            user_id: this.currentUserId,
            session_id: this.currentSessionId,
            project_id: selectedProjectId,
            token: token, // Add JWT token for authentication
            ...embedMessageFields(),
            ...(adminActAsUserId ? { admin_act_as_user_id: adminActAsUserId } : {}),
          };
          log.info(
            "WebSocket",
            "🔐 Sending authentication message with JWT token"
          );
          log.debug(
            "WebSocket",
            "📋 Current subscriptions:",
            this.subscriptionManager.getSubscribedProjects()
          );
          log.debug(
            "WebSocket",
            "WebSocket state before send:",
            this.ws?.readyState
          );
          log.debug(
            "WebSocket",
            "WebSocket readyState values: CONNECTING=0, OPEN=1, CLOSING=2, CLOSED=3"
          );
          this.socketIdentity = { socket, token, authSession: getAuthRuntimeSessionIdentity(), viewAs: adminActAsUserId };
          this.embedRefreshCleanup?.();
          if(embedRequested()){
            const refresh=()=>{
              if(this.ws!==socket||socket.readyState!==WebSocket.OPEN)return;
              const lease=getEmbedLease();
              if(!lease){socket.close(1008,'SAI Projects access expired');return;}
              socket.send(JSON.stringify({type:'sai-embed-refresh',saiProjectsGrant:lease.grant}));
            };
            window.addEventListener('sai-embed-lease-updated',refresh);
            this.embedRefreshCleanup=()=>window.removeEventListener('sai-embed-lease-updated',refresh);
          }
          this.send(authMessage);

        } else {
          log.debug(
            "WebSocket",
            "Missing authentication credentials during websocket open; closing without unauthenticated payloads"
          );
          if (this.ws === socket) {
            socket.close(1000, "Missing auth credentials");
          }
          return;
        }
        })();
      };

      socket.onmessage = (event) => {
        if (!this.isCurrentPrincipal(owner) || this.ws !== socket) {
          return;
        }

        try {
          const message: unknown = JSON.parse(event.data);
          if (!isWebSocketMsg(message)) {
            throw new Error('WebSocket message is missing a string type');
          }
          try {
            const authRejected = message.type === 'auth-invalidated' || (
              !this.socketAuthReady && message.type === 'error' &&
              ['Invalid authentication token', 'Missing authentication token', 'Invalid authentication data']
                .includes(String(message.message || message.error || ''))
            );
            if (authRejected) {
              const identity = this.socketIdentity;
              const currentToken = embedRequested() ? getEmbedLease()?.token : (getAuthRuntimeAccessToken() || getAuthToken());
              // A response for an older token/session cannot invalidate a replacement login.
              if (identity?.socket !== socket || identity.authSession !== getAuthRuntimeSessionIdentity() ||
                  identity.viewAs !== getAdminViewAsUserId() ||
                  ((message.type !== 'auth-invalidated' || identity.authSession === null) && identity.token !== currentToken) ||
                  (message.userId && message.userId !== this.currentUserId)) return;
              const userId = this.currentUserId;
              if (message.type === 'auth-invalidated') {
                // Permission changes can invalidate a socket while the account remains valid.
                // Preserve server-close reconnection, including an unchanged admin view-as owner.
                this.config.onMessage(message);
                return;
              }
              this.disconnect();
              this.config.onMessage({ type: 'auth-invalidated', userId, reason: 'websocket-authentication-rejected' });
              return;
            }
            if (message.type === "authenticated") {
              this.markSocketAuthenticated(socket);
            }
            if (!this.isCurrentPrincipal(owner) || this.ws !== socket) return;
            // No old projection/result callbacks may run before replacement authentication.
            if (!this.isConnected() && !['auth-error', 'error'].includes(message.type)) return;
            this.handleMessage(message);
          } catch (handlerError) {
            log.error("WebSocket", "Message handler failed:", handlerError);
            this.syncRecoveryManager.recoverFailedMessage(message);
            this.reportError(
              new Error(
                `Message handler failed: ${
                  handlerError instanceof Error
                    ? handlerError.message
                    : String(handlerError)
                }`
              )
            );
          }
        } catch (parseError) {
          // Message parsing failed - store for debugging and trigger recovery if needed
          const needsFullSync = this.syncRecoveryManager.recordUnparseableMessage(
            event.data,
            parseError instanceof Error
              ? parseError
              : new Error(String(parseError))
          );

          // Trigger full sync if too many parse failures
          if (needsFullSync) {
            this.syncRecoveryManager.initiateFullSync();
          }
        }
      };

      socket.onerror = () => {
        if (!this.isCurrentPrincipal(owner) || this.ws !== socket || this.manualDisconnect) {
          return;
        }

        // WebSocket errors are often followed by close events
        // Only log actual errors, not expected connection issues
        if (this.state === "connected") {
          // Unexpected error while connected
          log.warn("WebSocket", "Connection error occurred");
          this.reportError(new Error("WebSocket connection error"));
        }
        // For connecting/disconnected states, errors are expected during reconnection
        // These will be handled by the close event and reconnection logic
      };

      socket.onclose = () => {
        if (!this.isCurrentPrincipal(owner) || this.ws !== socket) return;
        this.ws = null;
        this.socketAuthReady = false;
        this.retainUnconfirmedSocketWrites(socket);
        if (!this.isCurrentPrincipal(owner) || this.ws) return;

        this.setState("disconnected");

        if (!this.manualDisconnect) {
          this.scheduleReconnect();
        }
      };
    } catch {
      // Connection failed - this is expected if backend is not running
      // Don't log error, scheduleReconnect will handle logging
      this.setState("disconnected");
      if (!this.manualDisconnect) {
        this.scheduleReconnect();
      }
    }
  }

  disconnect(): void {
    this.embedRefreshCleanup?.();this.embedRefreshCleanup=null;
    this.manualDisconnect = true;
    this.reconnectionManager.clearTimer();
    this.messageDeduplicator.clearCleanupTimer();
    this.stopHeartbeat();

    if (this.ws) {
      const socket = this.ws;
      this.ws = null;
      this.socketAuthReady = false;
      socket.close();
      this.retainUnconfirmedSocketWrites(socket);
    }

    this.setState("disconnected");
  }

  send(message: Record<string, unknown>): void | Promise<void> {
    if (message.type === 'operation') {
      if (!isNodeOperation(message.operation)) throw new Error('Invalid operation message');
      const envelopeId = typeof message.operationId === 'string' && message.operationId ? message.operationId : undefined;
      const { operationId } = resolveQueuedOperationIdentity({ ...message.operation }, envelopeId || `op_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`);
      const projectId = (typeof message.project_id === 'string' && message.project_id) ||
        (typeof message.projectId === 'string' && message.projectId) || undefined;
      const sessionId = (typeof message.session_id === 'string' && message.session_id) ||
        (typeof message.sessionId === 'string' && message.sessionId) || undefined;
      return this.admitOperation({
        id: operationId, operation: message.operation,
        projectId: projectId || this.subscriptionManager.getFirstProject() || 'default',
        sessionId: sessionId || this.currentSessionId || undefined,
        timestamp: typeof message.timestamp === 'number' ? message.timestamp : Date.now(),
        retries: 0, maxRetries: this.config.maxRetries,
      });
    }
    log.debug("WebSocket", "send() called with message type:", message.type);
    log.debug("WebSocket", "WebSocket state:", this.ws?.readyState);
    log.debug(
      "WebSocket",
      "WebSocket readyState values: CONNECTING=0, OPEN=1, CLOSING=2, CLOSED=3"
    );

    if (this.ws?.readyState === WebSocket.OPEN) {
      log.debug("WebSocket", "WebSocket is OPEN, sending message");
      this.ws.send(JSON.stringify({...message,...embedMessageFields()}));
      log.debug("WebSocket", "Message sent successfully");
    } else {
      log.warn(
        "WebSocket",
        "WebSocket not connected, queuing message. State:",
        this.ws?.readyState
      );
      const queuedMessages = this.queueManager.getMessageQueueLength() +
        [...this.userQueues.values()].reduce((count, saved) => count + saved.queue.getMessageQueueLength(), 0);
      if (queuedMessages >= this.MAX_QUEUE_SIZE) throw new Error('Message queue is full; message was not queued');
      this.queueManager.queueMessage(message);
    }
  }

  async sendOperation(operation: NodeOperation, projectId?: string, options: OperationSendOptions = {}): Promise<void> {
    const generatedOperationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const { operationId } = resolveQueuedOperationIdentity({ ...operation }, generatedOperationId);

    const queuedOp: QueuedOperation = {
      id: operationId,
      operation,
      timestamp: Date.now(),
      retries: 0,
      maxRetries: this.config.maxRetries,
      projectId:
        projectId || this.subscriptionManager.getFirstProject() || "default",
      sessionId: this.currentSessionId || undefined,
    };

    return this.admitOperation(queuedOp, options);
  }

  private async admitOperation(queuedOp: QueuedOperation, options: OperationSendOptions = {}): Promise<void> {
    const owner = options.owner ?? this.getPrincipalContext();
    if (this.switchingUser || !owner.userId || !this.isCurrentPrincipal(owner)) {
      throw new OperationJournalError('Operation principal is obsolete; operation was not admitted');
    }
    const key = this.operationKey(owner.userId, queuedOp.id, queuedOp.projectId);
    let admitted = false;
    let reserved = false;
    try {
      if (this.admittingOperations.has(key) || this.durableOperations.has(key)) {
        throw new OperationJournalError('Operation identity is already pending; reconcile before resubmitting');
      }
      // Snapshot caller-owned objects before the first await; transformations run once only.
      const wire = this.prepareWireOperation(queuedOp.operation, queuedOp.id);
      const visibleOperation = JSON.parse(JSON.stringify(queuedOp)) as QueuedOperation;
      const admission = this.journal.admit({ ownerId: owner.userId, projectId: queuedOp.projectId || '',
        operationId: queuedOp.id, operation: wire, timestamp: queuedOp.timestamp });
      this.admittingOperations.set(key, { owner, operation: visibleOperation });
      reserved = true;
      this.publishSyncState();
      const record = await admission;
      admitted = true;
      this.durableOperations.set(key, record);
      this.admittingOperations.delete(key);
      if (!this.isCurrentPrincipal(owner)) throw new OperationJournalError('Owner changed after durable admission; work retained for its owner', true);
      if (options.onCommitted) {
        const session = getAuthRuntimeSessionIdentity();
        const viewAs = getAdminViewAsUserId();
        this.commitListeners.set(key, { record, notify: options.onCommitted,
          current: () => this.isCurrentPrincipal(owner) && getAuthRuntimeSessionIdentity() === session && getAdminViewAsUserId() === viewAs });
      }
      this.journalErrors.delete(owner.userId);
      const retained = this.queuedFromRecord(record);
      if (!options.queueOnly && this.isConnected() && this.ws) {
        this.transmitTrackedOperation(retained, this.ws, owner);
      } else {
        this.queueManager.queueOperation(retained);
      }
    } catch (cause) {
      const error = cause instanceof OperationJournalError && cause.admitted === admitted
        ? cause : new OperationJournalError(admitted ? 'Durably admitted operation remains unresolved' : 'Operation admission failed', admitted, cause);
      if (admitted) this.quarantineOperation(this.queuedFromRecord(this.durableOperations.get(key)!), owner.userId, this.isCurrentPrincipal(owner));
      if (this.isCurrentPrincipal(owner)) {
        this.journalErrors.set(owner.userId, error);
        this.publishSyncState();
        if (this.isCurrentPrincipal(owner)) this.reportError(error);
      }
      throw error;
    } finally {
      if (reserved && this.admittingOperations.get(key)?.owner === owner) this.admittingOperations.delete(key);
      if (this.isCurrentPrincipal(owner)) this.publishSyncState();
    }
  }

  private queuedFromRecord(record: JournalRecord): QueuedOperation {
    return { id: record.operationId, projectId: record.projectId,
      operation: JSON.parse(JSON.stringify(record.operation)) as NodeOperation,
      timestamp: record.timestamp, retries: 0, maxRetries: this.config.maxRetries };
  }

  // Subscribe to a project for real-time updates
  subscribeToProject(
    projectId: string,
    forceResubscribe: boolean = false
  ): void {
    // If force flag is set, always send the subscribe message (used during reconnection)
    const isAlreadySubscribed = this.subscriptionManager.isSubscribed(projectId);
    if (forceResubscribe || !isAlreadySubscribed) {
      if (!isAlreadySubscribed) {
        this.subscriptionManager.subscribe(projectId);
      }

      log.debug(
        "WebSocket",
        `${
          forceResubscribe ? "Re-subscribing" : "Subscribing"
        } to project ${projectId}. Total subscriptions: ${
          this.subscriptionManager.getSubscriptionCount()
        }`
      );

      if (this.ws?.readyState === WebSocket.OPEN && this.socketAuthReady) {
        this.send({
          type: "subscribe-project",
          projectId,
          sessionId: this.currentSessionId,
        });
        if (!forceResubscribe) this.startReconciliation();
      } else {
        log.debug(
          "WebSocket",
          `Queued subscription to project ${projectId} (connection not ready)`
        );
      }
    }
  }

  // Unsubscribe from a project
  unsubscribeFromProject(projectId: string): void {
    const wasUnsubscribed = this.subscriptionManager.unsubscribe(projectId);

    if (wasUnsubscribed && this.ws?.readyState === WebSocket.OPEN && this.socketAuthReady) {
      this.send({
        type: "unsubscribe-project",
        projectId,
        sessionId: this.currentSessionId,
      });
    }
  }

  // Switch to a different project
  switchProject(newProjectId: string, oldProjectId?: string): void {
    if (oldProjectId && this.desiredProjects?.has(oldProjectId)) oldProjectId = undefined;
    if (oldProjectId && oldProjectId !== newProjectId) {
      this.unsubscribeFromProject(oldProjectId);
    }
    this.subscribeToProject(newProjectId);

    if (this.ws?.readyState === WebSocket.OPEN && this.socketAuthReady) {
      this.send({
        type: "switch-project",
        projectId: newProjectId,
        oldProjectId,
        sessionId: this.currentSessionId,
      });
    }
  }

  getSubscribedProjects(): string[] {
    return this.subscriptionManager.getSubscribedProjects();
  }

  private syncMessageQueue(): void {
    const socket = this.ws;
    const owner = this.getPrincipalContext();
    this.queueManager.syncMessageQueue((message) => {
      if (!socket || this.ws !== socket || !this.isCurrentPrincipal(owner) || !this.isConnected()) {
        throw new Error('Message connection changed before send');
      }
      socket.send(JSON.stringify({...message,...embedMessageFields()}));
    });
  }

  private prepareWireOperation(operation: NodeOperation, operationId: string): Record<string, unknown> {
    const stripNamespace = (id: string | undefined): string | undefined => id ? getNodeId(id) : id;
    const prepareNodePayload = (data: NodeOperation['data']): Record<string, unknown> | undefined => {
      if (!data) return undefined;
      const transformed = transformNodeToBackend(data);
      const nodeId = typeof transformed.id === 'string' ? stripNamespace(transformed.id) : undefined;
      const parentId = typeof transformed.parentId === 'string' ? stripNamespace(transformed.parentId) : undefined;
      const legacyParentId = typeof transformed.parent_id === 'string' ? stripNamespace(transformed.parent_id) : undefined;
      const dependencies = Array.isArray(transformed.dependencies)
        ? transformed.dependencies.filter((dependency: unknown): dependency is string => typeof dependency === 'string').map(stripNamespace)
        : undefined;
      return {
        ...transformed,
        ...(nodeId ? { id: nodeId } : {}),
        ...(parentId ? { parentId } : {}),
        ...(legacyParentId ? { parent_id: legacyParentId } : {}),
        ...(dependencies ? { dependencies } : {}),
      };
    };
    const wire: Record<string, unknown> = {
      ...operation,
      id: operationId,
      ...(operation.nodeId ? { nodeId: stripNamespace(operation.nodeId) } : {}),
      ...(operation.parentId ? { parentId: stripNamespace(operation.parentId) } : {}),
      ...(operation.targetParentId ? { targetParentId: stripNamespace(operation.targetParentId) } : {}),
      ...(operation.nodeIds ? { nodeIds: operation.nodeIds.map(stripNamespace) } : {}),
    };

    if (operation.data) {
      wire.data = prepareNodePayload(operation.data);
    }
    if (operation.updates) {
      wire.updates = operation.updates.map((update) => ({
        ...update,
        nodeId: stripNamespace(update.nodeId),
        data: prepareNodePayload(update.data),
      }));
    }
    return wire;
  }

  private handleMessage(message: WebSocketMsg): void {
    const owner = this.getPrincipalContext();
    const socket = this.ws;
    // CRITICAL LOGGING: Track all incoming messages
    log.debug("WebSocket", "📨 Received message:", {
      type: message.type,
      projectId: message.projectId,
      operation: message.operation && typeof message.operation.type === 'string' ? message.operation.type : undefined,
      nodeId: message.operation && typeof message.operation.nodeId === 'string' ? message.operation.nodeId : undefined,
    });

    // Confirm transport before optional projection callbacks or deduplication.
    this.acceptOperationAcknowledgement(message);
    if (!this.isCurrentPrincipal(owner) || this.ws !== socket) return;

    // Message deduplication
    const messageId = this.messageDeduplicator.getMessageId(message);
    if (this.messageDeduplicator.isDuplicate(messageId)) {
      log.debug("WebSocket", `Duplicate message filtered: ${messageId}`);
      return;
    }

    // Mark as processed
    this.messageDeduplicator.markAsProcessed(messageId);

    // Filter messages by project subscription
    log.debug("WebSocket", "🔍 Subscription check:", {
      messageProjectId: message.projectId,
      subscribedProjects: this.subscriptionManager.getSubscribedProjects(),
      currentUserId: this.currentUserId,
      messageUserId: message.userId,
    });

    if (
      this.subscriptionManager.shouldFilterMessage(
        message.projectId,
        typeof message.userId === 'string' ? message.userId : undefined,
        this.currentUserId
      )
    ) {
      return;
    }

    log.debug("WebSocket", "🚀 Processing message through onMessage handler");
    this.config.onMessage(message);
    if (!this.isCurrentPrincipal(owner) || this.ws !== socket) return;

    // Dispatch to appropriate handler
    const context: MessageHandlerContext = {
      subscribedProjects: new Set(
        this.subscriptionManager.getSubscribedProjects()
      ),
      currentUserId: this.currentUserId,
      currentSessionId: this.currentSessionId,
      reconnectAttempts: this.reconnectionManager.getAttemptCount(),
      maxRetries: this.config.maxRetries,
      startHeartbeat: () => this.startHeartbeat(),
      updateSyncState: (updates) => this.updateSyncState(updates),
      setState: (state) => this.setState(state),
      clearReconnectTimer: () => {
        this.reconnectionManager.clearTimer();
        this.reconnectionManager.setAttemptCount(this.config.maxRetries || 3);
        this.currentSessionId = null;
        this.currentUserId = null;
      },
    };

    dispatchMessage(message, context);
  }

  private markSocketAuthenticated(socket: WebSocket): void {
    if (this.ws !== socket || socket.readyState !== WebSocket.OPEN || this.socketAuthReady) {
      return;
    }

    const owner = this.getPrincipalContext();
    this.socketAuthReady = true;
    this.reconnectionManager.resetAttempts();
    this.setState("connected");
    if (!this.isCurrentPrincipal(owner) || this.ws !== socket || !this.isConnected()) return;
    this.updateSyncState({ lastSyncTime: Date.now() });
    if (!this.isCurrentPrincipal(owner) || this.ws !== socket || !this.isConnected()) return;

    const subscriptionCount = this.subscriptionManager.getSubscriptionCount();
    if (subscriptionCount > 0) {
      log.info(
        "WebSocket",
        "🔄 Re-subscribing to",
        subscriptionCount,
        "projects after authentication"
      );
      this.subscriptionManager.getSubscribedProjects().forEach((projectId) => {
        this.subscribeToProject(projectId, true);
      });
    }

    this.startHeartbeat();
    this.syncMessageQueue();
    this.startReconciliation();
  }

  private startHeartbeat(): void {
    this.heartbeatManager.start(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({
          type: "ping",
          session_id: this.currentSessionId,
        });
      }
    });
  }

  private stopHeartbeat(): void {
    this.heartbeatManager.stop();
  }

  private reportError(error: Error): void {
    try { this.config.onError(error); }
    catch { log.error('WebSocket', 'Error subscriber failed'); }
  }

  private setState(newState: WebSocketState): void {
    if (this.state !== newState) {
      this.state = newState;
      if (this.switchingUser) return;
      try { this.config.onStateChange(newState); }
      catch { log.error('WebSocket', 'State subscriber failed'); }
    }
  }

  private operationKey(ownerId: string, operationId: string, projectId?: string): string {
    return JSON.stringify([ownerId, projectId, operationId]);
  }

  private transmitTrackedOperation(operation: QueuedOperation, socket: WebSocket, owner: PrincipalContext): void {
    if (!owner.userId || !this.isCurrentPrincipal(owner) || this.ws !== socket || !this.isConnected()) {
      throw new Error('Operation connection changed before send');
    }
    const record = this.durableOperations.get(this.operationKey(owner.userId, operation.id, operation.projectId));
    if (!record) throw new OperationJournalError('Operation has no durable admission');
    const tracked = { ...operation };
    const wire = JSON.stringify({
      type: 'operation', operation: record.operation,
      project_id: operation.projectId, session_id: this.currentSessionId,
      timestamp: operation.timestamp, operationId: operation.id,
      ...embedMessageFields(),
    });
    const key = this.operationKey(owner.userId, operation.id, operation.projectId);
    // Only transport session metadata changes; the persisted mutation stays exact.
    this.acknowledgementSessions.set(key, this.currentSessionId || operation.sessionId || '');
    const timer = setTimeout(() => {
      const pending = this.awaitingAcknowledgements.get(key);
      if (!pending) return;
      this.awaitingAcknowledgements.delete(key);
      this.quarantineOperation(pending.operation, pending.ownerId);
      if (this.isCurrentPrincipal(owner)) this.reportError(new Error('Operation acknowledgement timed out; reload to reconcile'));
    }, this.ACKNOWLEDGEMENT_TIMEOUT_MS);
    this.awaitingAcknowledgements.set(key, { operation: tracked, ownerId: owner.userId, socket, timer });
    this.publishSyncState();
    try {
      if (!this.isCurrentPrincipal(owner) || this.ws !== socket || !this.isConnected()) {
        throw new Error('Operation connection changed before send');
      }
      socket.send(wire);
    } catch (error) {
      clearTimeout(timer);
      this.awaitingAcknowledgements.delete(key);
      throw error;
    }
  }

  private retainUnconfirmedSocketWrites(socket: WebSocket): void {
    for (const [key, pending] of this.awaitingAcknowledgements) {
      if (pending.socket !== socket) continue;
      clearTimeout(pending.timer);
      this.awaitingAcknowledgements.delete(key);
      this.quarantineOperation(pending.operation, pending.ownerId);
    }
  }

  private acceptOperationAcknowledgement(message: WebSocketMsg): void {
    if (!this.isConnected() || !this.currentUserId || !['operation-confirmed', 'operation-result', 'operation-error'].includes(message.type)) return;
    const nested = message.operation?.operation;
    const op = nested && typeof nested === 'object' && !Array.isArray(nested)
      ? nested as Record<string, unknown> : message.operation;
    const id = message.operationId || (typeof op?.id === 'string' ? op.id : null);
    if (!id) return;
    const key = this.operationKey(this.currentUserId, id, message.projectId);
    const pending = this.awaitingAcknowledgements.get(key);
    const uncertain = this.uncertainOperations.get(this.currentUserId) ?? [];
    const tracked = pending?.operation || uncertain.find(operation => operation.id === id && operation.projectId === message.projectId);
    if (!tracked || message.projectId !== tracked.projectId || message.sessionId !== this.acknowledgementSessions.get(key)) return;
    if (message.type === 'operation-error') {
      if (pending) { clearTimeout(pending.timer); this.awaitingAcknowledgements.delete(key); }
      this.quarantineOperation(tracked, this.currentUserId);
      this.startReconciliation();
      return;
    }
    if (message.result?.success !== true) return;
    // A successful transport ACK is not a transactional receipt. Read back exact payload.
    this.startReconciliation();
  }

  private startReconciliation(): void {
    if (this.ws && this.reconciliationJobs.has(this.ws)) this.reconciliationRequested.add(this.ws);
    // reconcileJournal publishes the error and remains rejecting for explicit callers.
    void this.reconcileJournal().catch(() => {});
  }

  /** Readback is retryable by callers; neither network nor storage rejection drops work. */
  reconcileJournal(): Promise<void> {
    const socket = this.ws;
    if (!socket || !this.isConnected()) return Promise.reject(new OperationJournalError('Authenticate before journal reconciliation', true));
    const existing = this.reconciliationJobs.get(socket);
    if (existing) return existing;
    const owner = this.getPrincipalContext();
    const authSession = getAuthRuntimeSessionIdentity();
    const job = this.reconcileAuthenticatedJournal(socket).catch(cause => {
      const error = cause instanceof OperationJournalError ? cause : new OperationJournalError('Journal reconciliation failed; work remains unresolved', true, cause);
      if (owner.userId && this.isCurrentPrincipal(owner) && this.ws === socket && this.isConnected() && getAuthRuntimeSessionIdentity() === authSession) {
        this.journalErrors.set(owner.userId, error);
        this.publishSyncState();
        if (this.isCurrentPrincipal(owner) && this.ws === socket) this.reportError(error);
      }
      throw error;
    }).finally(() => {
      this.reconciliationJobs.delete(socket);
      if (this.reconciliationRequested.delete(socket) && this.ws === socket && this.isConnected()) this.startReconciliation();
    });
    this.reconciliationJobs.set(socket, job);
    return job;
  }

  private async reconcileAuthenticatedJournal(socket: WebSocket): Promise<void> {
    const owner = this.getPrincipalContext();
    const sessionId = this.currentSessionId;
    const authSession = getAuthRuntimeSessionIdentity();
    const token = getAuthRuntimeAccessToken() || getAuthToken();
    const viewAs = getAdminViewAsUserId();
    const current = () => this.isCurrentPrincipal(owner) && this.ws === socket && this.isConnected() &&
      this.currentSessionId === sessionId && getAuthRuntimeSessionIdentity() === authSession &&
      getAdminViewAsUserId() === viewAs && (getAuthRuntimeAccessToken() || getAuthToken()) === token;
    if (!owner.userId || !token || !current()) return;
    const ownerQueue = this.queueManager;
    const records = await this.journal.listOwner(owner.userId);
    if (!current()) return;
    for (const record of records) this.durableOperations.set(journalKey(record), record);
    this.publishSyncState();
    if (!current()) return;
    const projects = new Map<string, JournalRecord[]>();
    for (const record of records) {
      // Access/subscription removals must not permit stale payload callbacks or replay.
      if (!this.subscriptionManager.isSubscribed(record.projectId)) continue;
      const batch = projects.get(record.projectId) ?? [];
      batch.push(record); projects.set(record.projectId, batch);
    }
    const refreshedProjects = new Set<string>();
    for (const [projectId, projectRecords] of projects) {
      for (let offset = 0; offset < projectRecords.length; offset += 100) {
        if (!current() || !this.subscriptionManager.isSubscribed(projectId)) return;
        const batch = projectRecords.slice(offset, offset + 100);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15_000);
        let data: ReturnType<typeof validateReceiptQuery>;
        try {
          // Capture credentials once; no generic refresh/retry may substitute another owner.
          const response = await fetch(`${API_BASE_URL}/api/v1/projects/${encodeURIComponent(projectId)}/operation-receipts/query`, {
            method: 'POST', credentials: 'omit', signal: controller.signal,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`,
              ...(viewAs ? { 'X-PM-Admin-Act-As-User-Id': viewAs } : {}) },
            body: JSON.stringify({ operations: batch.map(record => ({ operation_id: record.operationId, operation: record.operation })) }),
          });
          if (!current() || !this.subscriptionManager.isSubscribed(projectId)) return;
          if (!response.ok) throw new OperationJournalError(`Receipt query failed (${response.status}); work remains unresolved`, true);
          const payload: unknown = await response.json();
          if (!current() || !this.subscriptionManager.isSubscribed(projectId)) return;
          data = validateReceiptQuery(payload, batch);
        } finally { clearTimeout(timeout); }
        for (const receipt of data.receipts) {
          if (!current() || !this.subscriptionManager.isSubscribed(projectId)) return;
          const record = batch.find(item => item.operationId === receipt.operationId)!;
          if (receipt.outcome !== 'committed') { this.quarantineOperation(this.queuedFromRecord(record), owner.userId); continue; }
          const bridge = (typeof window !== 'undefined' ? (window as unknown as {
            __multiProjectContext?: { getRecoveryOwnerEpoch?: () => string | null; reloadProjects?: (ids: string[], epoch: string) => Promise<void> };
          }).__multiProjectContext : undefined);
          const epoch = bridge?.getRecoveryOwnerEpoch?.();
          if (epoch && bridge?.reloadProjects && !refreshedProjects.has(projectId)) {
            // Recovery after a reload can commit after the initial project read.
            // Refresh canonical state before forgetting the recovered operation.
            const completion = bridge.reloadProjects([projectId], epoch);
            if (!completion || typeof completion.then !== 'function') throw new OperationJournalError('Project reload did not confirm completion', true);
            await completion;
            refreshedProjects.add(projectId);
            if (!current() || !this.subscriptionManager.isSubscribed(projectId)) return;
          }
          await this.journal.removeCommitted(record);
          // Finish only this owner's bookkeeping even after a switch; no UI callbacks.
          this.clearCommittedMemory(record, ownerQueue);
          if (!current()) { this.commitListeners.delete(journalKey(record)); return; }
          this.notifyCommittedWrite(record);
          this.publishSyncState();
        }
        for (const id of data.missing) {
          if (!current() || !this.subscriptionManager.isSubscribed(projectId)) return;
          const record = batch.find(item => item.operationId === id)!;
          const key = journalKey(record);
          if (this.awaitingAcknowledgements.has(key)) continue;
          if (!this.transactionalReceiptsQualified) {
            this.quarantineOperation(this.queuedFromRecord(record), owner.userId); continue;
          }
          try {
            this.removeQueuedRecord(record);
            this.transmitTrackedOperation(this.queuedFromRecord(record), socket, owner);
          } catch (cause) {
            this.quarantineOperation(this.queuedFromRecord(record), owner.userId);
            throw new OperationJournalError('Receipt retry remains unresolved', true, cause);
          }
        }
      }
    }
    if (current()) { this.journalErrors.delete(owner.userId); this.publishSyncState(); }
  }

  async reviewOperations(): Promise<OperationReviewItem[]> {
    const owner = this.getPrincipalContext();
    const session = getAuthRuntimeSessionIdentity();
    const token = getAuthRuntimeAccessToken() || getAuthToken();
    const viewAs = getAdminViewAsUserId();
    const current = () => this.isCurrentPrincipal(owner) && getAuthRuntimeSessionIdentity() === session
      && getAdminViewAsUserId() === viewAs && (getAuthRuntimeAccessToken() || getAuthToken()) === token;
    if (!owner.userId || !token || !current()) throw new OperationJournalError('Authenticate before reviewing pending changes', true);
    const records = await this.journal.listOwner(owner.userId);
    if (!current()) throw new OperationJournalError('The active owner changed during review', true);
    const output: OperationReviewItem[] = [];
    const byProject = new Map<string, JournalRecord[]>();
    for (const record of records) {
      const rows = byProject.get(record.projectId) ?? [];
      rows.push(record); byProject.set(record.projectId, rows);
    }
    for (const [projectId, rows] of byProject) {
      for (let offset=0; offset<rows.length; offset+=100) {
        const batch=rows.slice(offset,offset+100);
        const controller=new AbortController();
        const timer=setTimeout(()=>controller.abort(),15_000);
        try {
          if (!current()) throw new OperationJournalError('The active owner changed during review', true);
          const response=await fetch(`${API_BASE_URL}/api/v1/projects/${encodeURIComponent(projectId)}/operation-receipts/query`, {
            method:'POST',credentials:'omit',signal:controller.signal,
            headers:{'Content-Type':'application/json',Authorization:`Bearer ${token}`,...(viewAs?{'X-PM-Admin-Act-As-User-Id':viewAs}:{})},
            body:JSON.stringify({settle_invalid:true,operations:batch.map(record=>({operation_id:record.operationId,operation:record.operation}))}),
          });
          if (!response.ok) throw new OperationJournalError(`Pending change review failed (${response.status})`,true);
          const classified=validateReceiptQuery(await response.json(),batch);
          if (!current()) throw new OperationJournalError('The active owner changed during review',true);
          for (const record of batch) output.push({owner,projectId,operationId:record.operationId,
            operation:JSON.parse(JSON.stringify(record.operation)),timestamp:record.timestamp,
            outcome:classified.receipts.find(item=>item.operationId===record.operationId)?.outcome ?? 'unconfirmed'});
        } finally { clearTimeout(timer); }
      }
    }
    return output;
  }

  async keepServerVersion(reviewed: OperationReviewItem): Promise<void> {
    const owner=reviewed.owner;
    if (!this.isCurrentPrincipal(owner)) throw new OperationJournalError('The active owner changed',true);
    const session=getAuthRuntimeSessionIdentity();
    const viewAs=getAdminViewAsUserId();
    const ownerQueue=this.queueManager;
    const current=()=>this.isCurrentPrincipal(owner)&&getAuthRuntimeSessionIdentity()===session&&getAdminViewAsUserId()===viewAs;
    const latest=(await this.reviewOperations()).find(item=>item.projectId===reviewed.projectId&&item.operationId===reviewed.operationId);
    if (!latest || latest.outcome==='unconfirmed') throw new OperationJournalError('An unconfirmed change cannot be dismissed',true);
    if (JSON.stringify(latest.operation)!==JSON.stringify(reviewed.operation)) throw new OperationJournalError('Pending change was replaced; review it again',true);
    const record=(await this.journal.listOwner(owner.userId!)).find(item=>item.projectId===reviewed.projectId&&item.operationId===reviewed.operationId);
    if (!record || !current()) throw new OperationJournalError('The active owner or pending change changed',true);
    const bridge=(window as unknown as {__multiProjectContext?:{getRecoveryOwnerEpoch?:()=>string|null;reloadProjects?:(ids:string[],epoch:string)=>Promise<void>}}).__multiProjectContext;
    const epoch=bridge?.getRecoveryOwnerEpoch?.();
    if (!epoch || !bridge?.reloadProjects) throw new OperationJournalError('Project readback is unavailable; pending change is retained',true);
    const completion=bridge.reloadProjects([record.projectId],epoch);
    if (!completion || typeof completion.then!=='function') throw new OperationJournalError('Project readback did not confirm completion',true);
    await completion;
    if (!current() || bridge.getRecoveryOwnerEpoch?.()!==epoch) throw new OperationJournalError('The active owner changed during readback',true);
    await this.journal.removeCommitted(record);
    this.clearCommittedMemory(record, ownerQueue);
    if (current()) { this.notifyCommittedWrite(record); this.journalErrors.delete(owner.userId!); this.publishSyncState(); }
    else this.commitListeners.delete(journalKey(record));
  }

  private notifyCommittedWrite(record: JournalRecord): void {
    const key = journalKey(record);
    const listener = this.commitListeners.get(key);
    this.commitListeners.delete(key);
    if (!listener || !listener.current() || JSON.stringify(listener.record.operation) !== JSON.stringify(record.operation)) return;
    try { listener.notify(); }
    catch { log.error('WebSocket', 'Committed write subscriber failed'); }
  }

  private removeQueuedRecord(record: JournalRecord, queue = this.queueManager): void {
    for (const queued of queue.getQueuedOperations()) {
      if (queued.id === record.operationId && queued.projectId === record.projectId) queue.removeOperation(queued);
    }
  }

  private clearCommittedMemory(record: JournalRecord, queue: QueueManager): void {
    const key = journalKey(record);
    this.durableOperations.delete(key);
    const pending = this.awaitingAcknowledgements.get(key);
    if (pending) clearTimeout(pending.timer);
    this.awaitingAcknowledgements.delete(key);
    this.acknowledgementSessions.delete(key);
    this.removeQueuedRecord(record, queue);
    const remaining = (this.uncertainOperations.get(record.ownerId) ?? []).filter(item => item.id !== record.operationId || item.projectId !== record.projectId);
    if (remaining.length) this.uncertainOperations.set(record.ownerId, remaining);
    else this.uncertainOperations.delete(record.ownerId);
  }

  private quarantineOperation(operation: QueuedOperation, ownerId = this.currentUserId, publish = true): void {
    if (!ownerId) return;
    const uncertain = this.uncertainOperations.get(ownerId) ?? [];
    if (!uncertain.some(existing => existing.id === operation.id && existing.projectId === operation.projectId)) uncertain.push(operation);
    this.uncertainOperations.set(ownerId, uncertain);
    if (publish && ownerId === this.currentUserId) this.publishSyncState();
  }

  getRecoveryState() {
    return this.syncRecoveryManager.getRecoveryState();
  }

  async retryRecovery(): Promise<void> {
    await this.syncRecoveryManager.retryRecovery();
    if (this.isConnected()) await this.reconcileJournal();
  }

  private publishSyncState(): void {
    const recovery = this.syncRecoveryManager.getRecoveryState().status;
    const hasUncertainWrites = Boolean(this.currentUserId && this.uncertainOperations.get(this.currentUserId)?.length);
    const queued = this.queueManager.getQueuedOperations();
    const awaiting = [...this.awaitingAcknowledgements.values()]
      .filter(pending => pending.ownerId === this.currentUserId).map(pending => pending.operation);
    const uncertain = this.currentUserId ? this.uncertainOperations.get(this.currentUserId) ?? [] : [];
    const durable = [...this.durableOperations.values()].filter(record => record.ownerId === this.currentUserId).map(record => this.queuedFromRecord(record));
    const admitting = [...this.admittingOperations.values()].filter(item => this.isCurrentPrincipal(item.owner)).map(item => item.operation);
    const pendingOperations = [...new Map([...queued, ...awaiting, ...uncertain, ...durable, ...admitting].map(operation => [this.operationKey(this.currentUserId || '', operation.id, operation.projectId), operation])).values()];
    const syncStatus = Boolean(this.currentUserId && this.journalErrors.has(this.currentUserId)) || hasUncertainWrites || this.outboundSyncStatus === 'conflict' || recovery === 'conflict'
      ? 'conflict' : recovery === 'syncing' ? 'syncing' : pendingOperations.length ? 'pending' : 'synced';
    this.syncState = { ...this.syncState, pendingOperations, syncStatus };
    if (this.switchingUser) return;
    try { this.config.onSyncStateChange(this.getSyncState()); }
    catch { log.error('WebSocket', 'Sync state subscriber failed'); }
  }

  private updateSyncState(updates: Partial<SyncState>): void {
    if (updates.syncStatus) this.outboundSyncStatus = updates.syncStatus;
    this.syncState = { ...this.syncState, ...updates };
    this.publishSyncState();
  }

  private scheduleReconnect(): void {
    const hasCredentials =
      !!this.currentSessionId && !!this.currentUserId;
    const shouldReconnect = this.reconnectionManager.scheduleReconnect(
      hasCredentials,
      () => this.connect()
    );

    if (!shouldReconnect) {
      this.setState("disconnected");
    }
  }

  getState(): WebSocketState {
    return this.state;
  }

  getSyncState(): SyncState {
    return { ...this.syncState, pendingOperations: [...this.syncState.pendingOperations] };
  }

  getQueueLength(): number {
    return this.queueManager.getOperationQueueLength();
  }

  flushQueuedOperations(): void {
    if (this.isConnected() && this.state !== 'syncing') {
      this.startReconciliation();
    }
  }

  isConnected(): boolean {
    const identity = this.socketIdentity;
    return this.ws?.readyState === WebSocket.OPEN && this.socketAuthReady && identity?.socket === this.ws &&
      identity.authSession === getAuthRuntimeSessionIdentity() && identity.viewAs === getAdminViewAsUserId() &&
      Boolean(getAuthRuntimeAccessToken() || getAuthToken()) &&
      (identity.authSession !== null || identity.token === (getAuthRuntimeAccessToken() || getAuthToken()));
  }
}
